import { Button, Divider, Flex, notification, Popconfirm, Statistic, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store.ts'
import React, { useState } from 'react'
import { useRunDataMigrationMutation } from '@/features/user/user.api.ts'
import { handleApiError } from '@/utils/api.tsx'
import { TimeTravelPanel } from '@/common/components/TimeTravelPanel.tsx'
import { TimeOffTestPanel } from '@/common/components/TimeOffTestPanel.tsx'
import { MissingEmployeesInAuthServiceModal } from '@/common/components/MissingEmployeesInAuthServiceModal.tsx'
import { useTriggerSelfServiceChangeNotificationMutation } from '@/features/employee-data/employee-data.api.ts'

type DetailCountdownProps = {
  title: string | React.ReactNode
  deadline: Dayjs
  showDay?: boolean
}

const DetailCountdown: React.FC<DetailCountdownProps> = ({ title, deadline, showDay = false }) => {
  return (
    <Flex gap="small" vertical className="mt-4 border w-fit p-4 rounded-xl">
      {typeof title === 'string' ? <Typography.Title level={3}>{title}</Typography.Title> : title}
      <Flex gap="small" align="center">
        <Statistic.Timer
          type="countdown"
          value={deadline.toString()}
          format="HH:mm:ss"
        />
      </Flex>
      {
        showDay ?
          <Flex gap="small">
            <Statistic.Timer
              type="countdown"
              title="Months"
              value={deadline.toString()}
              format="M"
            />
            <Statistic.Timer
              type="countdown"
              title="Days"
              value={deadline.toString()}
              format="D"
            />
          </Flex>
          : <Flex gap="small">
            <Statistic.Timer
              type="countdown"
              title="Hours"
              value={deadline.toString()}
              format="H"
            />
            <Statistic.Timer
              type="countdown"
              title="Minutes"
              value={deadline.toString()}
              format="m"
            />
            <Statistic.Timer
              type="countdown"
              title="Seconds"
              value={deadline.toString()}
              format="s"
            />
          </Flex>
      }
    </Flex>
  )
}

const allowedUsers = ['mlun', 'who', 'naldeken']

export const DevPanel: React.FC = () => {
  const [showPanel, setShowPanel] = useState(false)
  const { user } = useSelector((state: RootState) => state.auth)
  const haveAccess = user && allowedUsers.includes(user.name)
  const [runDataMigration] = useRunDataMigrationMutation()
  const [triggerSelfServiceChangeNotification, { isLoading: isTriggeringNotification }] =
    useTriggerSelfServiceChangeNotificationMutation()

  const handleRunDataMigration = async () => {
    try {
      await runDataMigration().unwrap()
      notification.success({ message: 'Data migration execute successfully' })
    } catch (e) {
      handleApiError(e)
    }
  }

  const handleTriggerSelfServiceChangeNotification = async () => {
    try {
      await triggerSelfServiceChangeNotification().unwrap()
      notification.success({ message: 'Notification sent successfully' })
    } catch (e) {
      handleApiError(e)
    }
  }

  if (!haveAccess) return null

  if (!showPanel) {
    return <Button className="w-64 mb-4" type="primary" onClick={() => setShowPanel(true)}>
      Show Developer Panel
    </Button>
  }

  return (
    <Flex vertical gap="middle">
      <Button className="w-64 mb-4" danger type="primary" onClick={() => setShowPanel(false)}>
        Close Developer Panel
      </Button>
      <Flex gap="middle">
        <DetailCountdown title="Leaving office in" deadline={dayjs().hour(18).minute(0).second(0)} />
        <DetailCountdown title="Happy weekend in" deadline={dayjs().day(5).hour(18).minute(0).second(0)} />
      </Flex>

      <Divider orientation="left">Data Migration</Divider>
      <Flex gap="small" wrap="wrap">
        <Popconfirm
          title="Are you sure to run data migration?"
          okText="Yes"
          cancelText="No"
          onConfirm={handleRunDataMigration}
        >
          <Button className="w-64" type="primary">Run Data Migration</Button>
        </Popconfirm>
        <MissingEmployeesInAuthServiceModal />
      </Flex>

      <Divider orientation="left">Employee Profile</Divider>
      <Button
        className="w-64"
        type="primary"
        loading={isTriggeringNotification}
        onClick={handleTriggerSelfServiceChangeNotification}
      >
        Notify HC about Self Service Changes
      </Button>

      <Divider orientation="left">Time Travel</Divider>
      <TimeTravelPanel />

      <Divider orientation="left">Time Off Triggers</Divider>
      <TimeOffTestPanel />
    </Flex>
  )
}